import {
  PermitRequestSummaryPanel,
  type RiskSnapshot,
  type SummaryActionNotice,
} from "modules/permit/components/PermitRequestSummaryPanel"
import { Info } from "lucide-react"
import type { WorkerSelectionItem } from "modules/permit"
import type { WorkAreaSelection } from "modules/permit/components/WorkAreaSection"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { TemplateConfiguration } from "shared/api/templateService"
import type { PermitSummaryData, RequiredDocumentSummary } from "modules/permit/components/PermitRequestSummaryPanel"

interface PermitRequestSidebarProps {
  mode: "create" | "edit"
  isWorkflowTimelineLoading: boolean
  onOpenTimeline: () => void
  permitData: PermitSummaryData
  selectedWorkers: WorkerSelectionItem[]
  selectedActivities: string[]
  requiredDocuments: RequiredDocumentSummary[]
  uploadedFiles: UploadedFileItem[]
  riskSnapshot: RiskSnapshot
  workAreaSelection: WorkAreaSelection
  templateConfig: TemplateConfiguration | null
  permitStatusCode?: string | null
  permitStatusName?: string | null
  generalFieldLabels?: Record<string, string>
  actionNotice?: SummaryActionNotice | null
}

// Sidebar container: timeline button + live summary panel
export function PermitRequestSidebar({
  mode: _mode,
  isWorkflowTimelineLoading: _isWorkflowTimelineLoading,
  onOpenTimeline: _onOpenTimeline,
  permitData,
  selectedWorkers,
  selectedActivities,
  requiredDocuments,
  uploadedFiles,
  riskSnapshot,
  workAreaSelection,
  templateConfig,
  permitStatusCode,
  permitStatusName,
  generalFieldLabels,
  actionNotice,
}: PermitRequestSidebarProps) {
  return (
    <div className="flex w-full flex-col gap-4 lg:sticky lg:top-24 lg:h-[calc(100vh-120px)] lg:max-w-[480px] lg:flex-none lg:self-start">
      {actionNotice ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-800 shadow-sm">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4" />
            <div className="space-y-1">
              <p className="font-semibold">{actionNotice.title}</p>
              {actionNotice.message ? <p className="text-sm text-rose-900">{actionNotice.message}</p> : null}
              {actionNotice.actor || actionNotice.timestamp ? (
                <p className="text-xs text-rose-700">
                  {actionNotice.actor ? <span>โดย {actionNotice.actor}</span> : null}
                  {actionNotice.actor && actionNotice.timestamp ? <span> • </span> : null}
                  {actionNotice.timestamp ? <span>{actionNotice.timestamp}</span> : null}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <PermitRequestSummaryPanel
        className="min-h-0 flex-1"
        permitData={permitData}
        selectedWorkers={selectedWorkers}
        selectedActivities={selectedActivities}
        requiredDocuments={requiredDocuments}
        uploadedFiles={uploadedFiles}
        riskInfo={riskSnapshot}
        workAreaSelection={workAreaSelection}
        templateConfig={templateConfig}
        permitStatusCode={permitStatusCode}
        permitStatusName={permitStatusName}
        generalFieldLabels={generalFieldLabels}
        actionNotice={null}
      />
    </div>
  )
}
